import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  LayoutDashboard, FileText, Building, Users, Settings,
  Bell, Menu, X, LogOut, User, ChevronDown, Building2,
  ClipboardList, Shield, FolderTree, Scale, Headset, Ticket, BarChart3
} from 'lucide-react';

const AdminLayout = () => {
  const { user, empresa, logout, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // El estado del sidebar es decisión del usuario y se persiste en localStorage.
  // La primera vez que un usuario abre la app, arranca abierto. Después se respeta
  // lo que eligió la última vez. NUNCA se cambia automáticamente por viewport ni resize.
  const [sidebarOpen, setSidebarOpenState] = useState(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('ldrv:sidebar:open') : null;
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });
  const setSidebarOpen = (value) => {
    setSidebarOpenState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      try {
        window.localStorage.setItem('ldrv:sidebar:open', String(next));
      } catch {
        /* localStorage no disponible o cuota llena: persistencia opcional */
      }
      return next;
    });
  };
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const countResponse = await api.get('/alertas/contador');
        setAlertCount(countResponse.data.count);
      } catch (err) {
        console.error('Error fetching alerts:', err);
      }
    };
    
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    window.addEventListener('alertas-updated', fetchAlerts);
    return () => {
      clearInterval(interval);
      window.removeEventListener('alertas-updated', fetchAlerts);
    };
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { 
      path: '/admin/dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR', 'USUARIO', 'LEGAL']
    },
    { 
      path: '/admin/reclamos', 
      icon: FileText, 
      label: 'Reclamos',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR', 'USUARIO', 'LEGAL']
    },
    { 
      path: '/admin/resoluciones', 
      icon: Scale, 
      label: 'Resoluciones',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'LEGAL', 'SUPERVISOR', 'USUARIO']
    },
    { 
      path: '/admin/proyectos', 
      icon: FolderTree, 
      label: 'Proyectos',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR']
    },
    { 
      path: '/admin/usuarios', 
      icon: Users, 
      label: 'Usuarios',
      roles: ['SUPERADMIN', 'ADMINISTRADOR']
    },
    { 
      path: '/admin/empresa', 
      icon: Building, 
      label: 'Empresa',
      roles: ['SUPERADMIN', 'ADMINISTRADOR']
    },
    { 
      path: '/admin/empresas', 
      icon: Building2, 
      label: 'Empresas',
      roles: ['SUPERADMIN']
    },
    { 
      path: '/admin/auditoria', 
      icon: ClipboardList, 
      label: 'Auditoría',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR']
    },
  ];

  // ============================================================
  // Sub-items del módulo Centro de Ayuda (visible si la empresa
  // tiene `modulo_tickets_activo`). Es un grupo aparte para que en
  // el sidebar se rendericen separados con su propio encabezado.
  // ============================================================
  const centroAyudaItems = (empresa?.modulo_tickets_activo ? [
    {
      path: '/admin/centro-ayuda/tickets',
      icon: Ticket,
      label: 'Tickets',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR', 'USUARIO', 'LEGAL'],
    },
    {
      path: '/admin/centro-ayuda/configuracion',
      icon: Settings,
      label: 'Configuración',
      roles: ['SUPERADMIN', 'ADMINISTRADOR'],
    },
    {
      path: '/admin/centro-ayuda/estadisticas',
      icon: BarChart3,
      label: 'Estadísticas',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR'],
    },
    {
      path: '/admin/centro-ayuda/auditoria',
      icon: ClipboardList,
      label: 'Auditoría',
      roles: ['SUPERADMIN', 'ADMINISTRADOR', 'SUPERVISOR'],
    },
  ] : []).filter(it => it.roles.includes(user?.rol));

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.rol)
  );

  // Obtener colores del tema
  const themeColors = empresa?.colores_tema || {
    primario: '#C9A85C',
    secundario: '#C5B8A2',
    terciario: '#5A7A7A',
    cuaternario: '#5A7A7A',
    fondo: '#FFFFFF',
    superficie: '#F5F5F5',
    texto: '#333333',
    texto_secundario: '#666666',
    error: '#DC3545'
  };

  // Para compatibilidad con formato antiguo
  const sidebarColor = themeColors.terciario || themeColors.primario || '#5A7A7A';
  const accentColor = themeColors.primario || '#C9A85C';
  const hoverColor = themeColors.secundario || '#C5B8A2';
  const quaternaryColor = themeColors.cuaternario || '#5A7A7A';
  const errorColor = themeColors.error || '#DC3545';

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 shrink-0 transform transition-transform duration-200 ease-in-out overflow-hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:sticky lg:top-0 lg:h-screen lg:inset-auto lg:translate-x-0
          ${sidebarOpen ? 'lg:w-64' : 'lg:w-0 lg:border-0'}`}
        style={{ backgroundColor: sidebarColor, willChange: 'transform' }}
        aria-hidden={!sidebarOpen}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                {empresa?.logo_url ? (
                  <img src={empresa.logo_url} alt="Logo" className="w-8 h-8 object-contain" />
                ) : (
                  <Shield className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="text-white">
                <h1 className="font-heading font-bold text-sm">LDRV</h1>
                <p className="text-xs text-white/60">Admin Panel</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              style={{ touchAction: 'manipulation' }}
              onClick={() => setSidebarOpen(false)}
              aria-label="Cerrar menú"
              data-testid="btn-close-sidebar"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Empresa actual */}
          {empresa && (
            <div className="px-4 py-3 bg-white/5">
              <p className="text-xs text-white/50">Empresa actual</p>
              <p className="text-sm text-white font-medium truncate">{empresa.nombre}</p>
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-0.5">
              {filteredNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-lg cursor-pointer"
                    style={{
                      backgroundColor: isActive ? `${accentColor}` : 'transparent',
                      color: isActive ? 'white' : undefined,
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = `${hoverColor}80`;
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <item.icon className="w-[18px] h-[18px]" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}

              {/* Grupo: Centro de Ayuda (condicional por empresa) */}
              {centroAyudaItems.length > 0 && (
                <div className="pt-3 mt-1 border-t border-white/10" data-testid="sidebar-centro-ayuda">
                  <div className="px-3 py-1.5 flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/40">
                    <Headset className="w-3.5 h-3.5" />
                    <span>Centro de Ayuda</span>
                  </div>
                  {centroAyudaItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors rounded-lg cursor-pointer ml-1"
                        style={{
                          backgroundColor: isActive ? `${accentColor}` : 'transparent',
                          color: isActive ? 'white' : undefined,
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = `${hoverColor}80`;
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        data-testid={`sidebar-ca-${item.path.split('/').pop()}`}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="text-sm">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{user?.nombre}</p>
                <p className="text-xs text-white/50">{user?.rol}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b h-16 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            {!sidebarOpen && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                style={{ touchAction: 'manipulation' }}
                aria-label="Abrir menú"
                data-testid="btn-open-sidebar"
              >
                <Menu className="w-5 h-5" />
              </Button>
            )}
            <h1 className="font-heading text-lg font-semibold text-slate-900 hidden sm:block">
              {(() => {
                const path = location.pathname;
                const baseMatch = filteredNavItems.find(item => item.path === path);
                if (baseMatch) return baseMatch.label;
                if (path.startsWith('/admin/centro-ayuda/tickets/')) {
                  return 'Centro de Ayuda / Tickets';
                }
                const caMatch = centroAyudaItems.find(item => path.startsWith(item.path));
                if (caMatch) return `Centro de Ayuda / ${caMatch.label}`;
                return 'Panel';
              })()}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {/* Alerts */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative" 
              data-testid="alerts-btn"
              onClick={() => navigate('/admin/notificaciones')}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${quaternaryColor}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Bell className="w-5 h-5" />
              {alertCount > 0 && (
                <Badge 
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-white text-xs animate-pulse-ring"
                  style={{ backgroundColor: errorColor }}
                >
                  {alertCount > 9 ? '9+' : alertCount}
                </Badge>
              )}
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2" 
                  data-testid="user-menu-btn"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${quaternaryColor}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${sidebarColor}20` }}
                  >
                    <User className="w-4 h-4" style={{ color: sidebarColor }} />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">{user?.nombre}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user?.nombre}</p>
                    <p className="text-xs text-slate-500">{user?.correo}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => navigate('/admin/perfil')}
                  className="cursor-pointer"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${quaternaryColor}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <User className="w-4 h-4 mr-2" />
                  Mi Perfil
                </DropdownMenuItem>
                {user?.rol !== 'USUARIO' && (
                  <DropdownMenuItem 
                    onClick={() => navigate('/admin/empresa')}
                    className="cursor-pointer"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${quaternaryColor}40`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configuración
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout} 
                  className="cursor-pointer"
                  style={{ color: errorColor }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${errorColor}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className={`flex-1 ${location.pathname.startsWith('/admin/centro-ayuda/tickets') ? '' : 'p-4 lg:p-6'}`}>
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t bg-white py-4 px-6">
          <p className="text-xs text-slate-400 text-center">
            Libro de Reclamaciones Virtual © {new Date().getFullYear()} - {empresa?.nombre || 'Sistema LDRV'}
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;
